import React, { useState } from "react";
import {
  Modal,
  Stack,
  Box,
  Typography,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { TextArea } from "../Common/styles";
import FeedbackSelection from "./FeedbackSelection";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import {
  CaptureConsumerEngagementFeedbacks,
  RequestOtpForFeedback,
} from "../../graphql/mutation";
import {
  GetConsumerMappedForm,
  GetPresignedUrlforConsumerEngagement,
  VerifyOtp,
} from "../../graphql/queries";
import DialogModal from "../Common/DialogModal";
import AdditionalForms from "./Forms";
import axios from "axios";
import Submit from "./Submit";
import {
  getThankYouMessage,
  updateSessionData,
  validateEmail,
  validateMobile,
  startCountdown,
} from "./utils";
import Snackbar from "../Common/SnackBar";
import { LoadingButton } from "@mui/lab";

const FeedbackModal = ({
  setOpen,
  open,
  customerScanDetails,
  rating,
  setRating,
  thumbs,
  setThumbs,
  sessionId,
  traceItemID,
  isSubmitted,
  setIsSubmitted,
}) => {
  const handleClose = () => setOpen(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [mobile, setMobile] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [mobileError, setMobileError] = useState<string>("");
  const [otpVisible, setOtpVisible] = useState<boolean>(false);
  const [mobileOtpVisible, setMobileOtpVisible] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>("");
  const [mobileOtp, setMobileOtp] = useState<string>("");
  const [otpVerified, setOtpVerified] = useState<boolean>(false);
  const [thankYouMessage, setThankYouMessage] = useState<string>("");
  const [successOpen, setSuccessOpen] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [selectedFormFields, setSelectedFormFields] = useState({});
  const [emailOtpCountdown, setEmailOtpCountdown] = useState<number>(0);
  const [mobileOtpCountdown, setMobileOtpCountdown] = useState<number>(0);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const client = useApolloClient();

  const shouldShowAuth =
    customerScanDetails.authMethod === "authenticated" ||
    customerScanDetails.authMethod === "both";

  const shouldShowEmailAuth =
    shouldShowAuth &&
    (customerScanDetails.authType === "emailOtp" ||
      customerScanDetails.authType === "both") &&
    isSubmitted;

  const shouldShowMobileAuth =
    shouldShowAuth &&
    (customerScanDetails.authType === "mobileOtp" ||
      customerScanDetails.authType === "both") &&
    isSubmitted;

  const isAuthRequired =
    shouldShowAuth &&
    ((shouldShowEmailAuth && (!email || !otp)) ||
      (shouldShowMobileAuth && (!mobile || !mobileOtp)));

  // Get form mapped to consumer based on orgId and formId
  const { data: MappedFormData } = useQuery(GetConsumerMappedForm, {
    variables: {
      filters: {
        orgId: customerScanDetails.orgId,
        formName: customerScanDetails.formId,
      },
    },
    fetchPolicy: "network-only",
    skip: !customerScanDetails.orgId || !customerScanDetails.formId?.length,
  });

  // Feedback mutation
  const [CreateConsumerEngagement] = useMutation(
    CaptureConsumerEngagementFeedbacks
  );

  // Request OTP for feedback
  const [requestOtpForFeedback, { loading: otpLoading }] = useMutation(
    RequestOtpForFeedback,
    {
      variables: {
        otpRequest: {
          email,
        sessionId,
      },
    },
    fetchPolicy: "network-only",
  });

  const handleStarClick = (index) => {
    setRating(index + 1);
  };

  const handleThumbClick = (value) => {
    setThumbs(value);
  };

  const handleCommentChange = (event) => {
    if (event.target.value.length > 100) {
      setError("Comments should not exceed 100 characters.");
    } else {
      setError("");
      setComment(event.target.value);
    }
  };

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);
    setEmailError(validateEmail(value));
  };

  const handleMobileChange = (event) => {
    const value = event.target.value;
    setMobile(value);
    setMobileError(validateMobile(value));
  };

  const handleGetOtpClick = async () => {
    if (!emailError && email) {
      const response = await requestOtpForFeedback();
      setSnackbarOpen(true);
      setSnackbarSeverity(
        response.data.requestOtpForFeedback.exists ? "success" : "error"
      );
      setSnackbarMessage(response.data.requestOtpForFeedback.message);
      if (response.data.requestOtpForFeedback.exists) {
        setOtpVisible(true);
        startCountdown(setEmailOtpCountdown);
      }
    }
  };

  const handleGetMobileOtpClick = () => {
    if (!mobileError && mobile) {
      setMobileOtpVisible(true);
      startCountdown(setMobileOtpCountdown);
    }
  };

  // Function to upload image or file to server
  const handleFileUploadToServer = async (file) => {
    try {
      const { data } = await client.query({
        query: GetPresignedUrlforConsumerEngagement,
        variables: {
          imageInput: {
            urlInput: [
              {
                fieldName: file.name,
                fileType: file.type,
                orgId: customerScanDetails.orgId,
              },
            ],
          },
        },
        fetchPolicy: "network-only",
      });

      const presignedUrl = data?.getPresignedUrlForConsumerEngagmentPutObject?.presignedUrl?.[0];
      const fieldName = data?.getPresignedUrlForConsumerEngagmentPutObject?.fileName?.[0];

      if (presignedUrl) {
        await axios.put(presignedUrl, file, {
          headers: {
            "Content-Type": file.type,
          },
        });

        return fieldName;
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    }
    return null;
  };

  const processFormFields = async (fields) => {
    const processed = { ...fields };

    for (const sectionId in processed) {  
      for (const fieldId in processed[sectionId]) {
        const field = processed[sectionId][fieldId];

        if (field.fieldType === "fileupload" && Array.isArray(field.value)) {
          const uploadedFiles: any[] = [];
          for (const file of field.value) {
            if (file instanceof File) {
              const fieldName = await handleFileUploadToServer(file);
              if (fieldName) {
                uploadedFiles.push(file, fieldName);
              }
            }
          }
          if (uploadedFiles.length > 0) {
            processed[sectionId][fieldId] = {
              ...field,
              value: uploadedFiles,
            };
          }
        }
      }
    }

    return processed;
  };

  const handleSubmit = async () => {
    if (customerScanDetails.commentsFiled === "mandatoryOnLowRating") {
      if (
        (rating > 0 && rating <= 2 && !comment) ||
        (thumbs === "thumbsdown" && !comment)
      ) {
        setError("Please tell us more.");
        return;
      }
    }
    setError("");
    setLoading(true);

    try {
      // Process form fields with file uploads
      const processedFormFields = await processFormFields(selectedFormFields);

      const inputData = {
        sessionId: sessionId,
        consumerEngagementDefId: customerScanDetails.id,
        rating:
          customerScanDetails.feedbackMethod === "rating"
            ? rating.toString()
            : thumbs || "",
        ...(comment && { comments: comment }),
        ...(isFormValid &&
          Object.keys(processedFormFields).length > 0 && {
            formInput: processedFormFields,
          }),
      };

      await CreateConsumerEngagement({
        variables: {
          consumerEngagementFeedbackInput: inputData,
        },
      });

      setLoading(false);
      setRating(0);
      setThumbs(null);
      setComment("");

      // Get thank you message using the shared utility function
      const message = getThankYouMessage(
        customerScanDetails.feedbackMethod,
        rating,
        thumbs,
        customerScanDetails.submissionDisplayMessage
      );

      if(customerScanDetails.authMethod === "anonymous") setSuccessOpen(true);
      setThankYouMessage(message);

      // Update session data using the shared utility function
      updateSessionData(traceItemID);
      setIsSubmitted(true);
    } catch (error) {
      setLoading(false);
      console.error("Error submitting feedback:", error);
    }
  };

  const handleOtpVerify = async () => {
    setLoading(true);
    const response = await client.query({
      query: VerifyOtp,
      variables: {
        filters: {
          email,
          sessionId,
          otp: Number(otp),
        },
      },
      fetchPolicy: "network-only",
    });
    setSnackbarOpen(true);
    setSnackbarSeverity(response.data.verifyOtp.exists ? "success" : "error");
    setSnackbarMessage(response.data.verifyOtp.message);
    if (response.data.verifyOtp.exists) {
      setSuccessOpen(true);
      setOtpVerified(true);
    };
    setLoading(false);
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 300,
    maxHeight: "400px",
    overflowY: "auto",
    bgcolor: "background.paper",
    border: "1px solid #ccc",
    borderRadius: "8px",
    boxShadow: 24,
    p: 4,
    textAlign: "center",
  };

  return (
    <React.Fragment>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Stack>
            {!isSubmitted && (
              <CloseIcon
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  cursor: "pointer",
                  color: "red",
                }}
              />
            )}
            {!isSubmitted && (
              <>
                <FeedbackSelection
                  feedbackMethod={customerScanDetails.feedbackMethod}
                  rating={rating}
                  thumbs={thumbs}
                  handleStarClick={handleStarClick}
                  handleThumbClick={handleThumbClick}
                />
                <TextArea
                  minRows={3}
                  placeholder={`Please tell us more`}
                  name="comments"
                  value={comment}
                  onChange={handleCommentChange}
                  sx={{ width: "300px", mt: 1, resize: "none" }}
                />
                {error && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {error}
                  </Typography>
                )}
                <AdditionalForms
                  selectedFormFields={selectedFormFields}
                  setIsFormValid={setIsFormValid}
                  setSelectedFormFields={setSelectedFormFields}
                  formDetails={
                    MappedFormData?.getConsumerMappedFormBasedOnOrgid || []
                  }
                />
              </>
            )}
            {shouldShowEmailAuth && !otpVerified && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Please verify
                </Typography>
                <TextField
                  variant="outlined"
                  label="Email"
                  type="email"
                  id="email"
                  value={email}
                  onChange={handleEmailChange}
                  autoFocus
                  color="primary"
                  fullWidth
                  disabled={otpVisible}
                  InputProps={{
                    sx: {
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgb(25, 118, 210)",
                      },
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      "&.Mui-focused": {
                        color: "rgb(25, 118, 210)",
                      },
                    },
                  }}
                  sx={{ mt: 2 }}
                />
                {emailError && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {emailError}
                  </Typography>
                )}
                <LoadingButton
                  loading={otpLoading}
                  variant="contained"
                  disabled={
                    !!emailError ||
                    !email ||
                    emailOtpCountdown > 0
                  }
                  onClick={handleGetOtpClick}
                  sx={{
                    mt: 2,
                    background: "rgb(25, 118, 210)",
                    "&:hover": {
                      background: "rgb(25, 118, 210)",
                    },
                    "&:focus": {
                      background: "rgb(25, 118, 210)",
                    },
                    color: "white",
                    width: "50%",
                    alignSelf: "flex-end",
                  }}
                >
                  {otpVisible ? "Resend OTP" : "Get OTP"}
                </LoadingButton>
                {emailOtpCountdown > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 1,
                      alignSelf: "flex-end",
                      color: "rgb(25, 118, 210)",
                    }}
                  >
                  { `Resend OTP in ${Math.floor(emailOtpCountdown / 60)}:${
                     String(emailOtpCountdown % 60).padStart(2, '0')
                  }` }
                      
                  </Typography>
                )}
                {otpVisible && (
                  <TextField
                    variant="outlined"
                    label="Enter OTP"
                    type="text"
                    id="otp"
                    value={otp}
                    onChange={(event) => setOtp(event.target.value)}
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    InputProps={{
                      sx: {
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgb(25, 118, 210)",
                        },
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        "&.Mui-focused": {
                          color: "rgb(25, 118, 210)",
                        },
                      },
                    }}
                  />
                )}
              </>
            )}

            {shouldShowMobileAuth && !otpVerified && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Please verify
                </Typography>
                <TextField
                  variant="outlined"
                  label="Mobile"
                  id="mobile"
                  value={mobile}
                  onChange={handleMobileChange}
                  autoFocus
                  color="primary"
                  fullWidth
                  disabled={mobileOtpVisible}
                  InputProps={{
                    sx: {
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgb(25, 118, 210)",
                      },
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      "&.Mui-focused": {
                        color: "rgb(25, 118, 210)",
                      },
                    },
                  }}
                  sx={{ mt: 2 }}
                />
                {mobileError && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {mobileError}
                  </Typography>
                )}
                <LoadingButton
                  loading={otpLoading}
                  variant="contained"
                  disabled={
                    !!mobileError ||
                    !mobile ||
                    mobileOtpCountdown > 0
                  }
                  onClick={handleGetMobileOtpClick}
                  sx={{
                    mt: 2,
                    background: "rgb(25, 118, 210)",
                    "&:hover": {
                      background: "rgb(25, 118, 210)",
                    },
                    "&:focus": {
                      background: "rgb(25, 118, 210)",
                    },
                    color: "white",
                    width: "50%",
                    alignSelf: "flex-end",
                  }}
                >
                  {mobileOtpVisible ? "Resend OTP" : "Get OTP"}
                </LoadingButton>
                {mobileOtpCountdown > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 1,
                      alignSelf: "flex-end",
                      color: "rgb(25, 118, 210)",
                    }}
                  >
                    { `Resend OTP in ${Math.floor(emailOtpCountdown / 60)}:${
                     String(emailOtpCountdown % 60).padStart(2, '0')
                    }` }
                  </Typography>
                )}
                {mobileOtpVisible && (
                  <TextField
                    variant="outlined"
                    label="Enter OTP"
                    type="text"
                    id="mobileOtp"
                    value={mobileOtp}
                    onChange={(event) => setMobileOtp(event.target.value)}
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    InputProps={{
                      sx: {
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgb(25, 118, 210)",
                        },
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        "&.Mui-focused": {
                          color: "rgb(25, 118, 210)",
                        },
                      },
                    }}
                  />
                )}
              </>
            )}

            {!isSubmitted && (
              <Submit
                isFormValid={isFormValid}
                loading={loading}
                handleSubmit={handleSubmit}
                isModal={true}
                isOtp={shouldShowAuth}
              />
            )}
            {isSubmitted && shouldShowAuth && (
              <Submit
                isFormValid={isFormValid}
                shouldShowAuth={shouldShowAuth}
                isAuthRequired={isAuthRequired}
                loading={loading}
                handleSubmit={handleOtpVerify}
                isOtp={true}
              />
              )}
          </Stack>
        </Box>
      </Modal>
      <DialogModal
        open={successOpen}
        setOpen={setSuccessOpen}
        message={thankYouMessage}
        title="Thank you for your feedback!"
        onClose={handleClose}
      />
      <Snackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </React.Fragment>
  );
};

export default FeedbackModal;
